import { ReactNode } from 'react';
import { IconFileWithItemsOutline, IconPlus, IconUpload } from '@iwoca/icons';
import { Button, Card, ErrorMessage, FileDrop, Label } from '@iwoca/orion';
import clsx from 'clsx';
import styles from './FileUpload.module.css';

export type TFileUploadValue<TDoc extends string = string> = {
  file: File;
  docType: TDoc;
};

type TProps<TDoc extends string> = {
  docType: TDoc;
  label: ReactNode;
  instruction?: ReactNode;
  errorMessage?: string;
  uploadedLabel?: ReactNode;
  fileDropClassName?: string;
  cardClassName?: string;
  disabled?: boolean;
  multiple?: boolean;
  selectedFiles?: TFileUploadValue<TDoc>[];
  onChange: (files: TFileUploadValue<TDoc>[]) => void;
};

export const FileUpload = <TDoc extends string>({
  docType,
  label,
  instruction,
  errorMessage,
  uploadedLabel = 'Document uploaded',
  fileDropClassName,
  cardClassName,
  disabled = false,
  multiple = false,
  selectedFiles = [],
  onChange,
}: TProps<TDoc>) => {
  const handleOnChange = (files: File[]) => {
    const uploaded = files.map((file) => ({ file, docType }));

    onChange(multiple ? [...selectedFiles, ...uploaded] : uploaded);
  };

  const handleRemove = (index: number) => {
    onChange(selectedFiles.filter((_, fileIndex) => fileIndex !== index));
  };

  const showFileDrop = multiple || selectedFiles.length === 0;

  const renderFileRow = (
    selectedFile: TFileUploadValue<TDoc>,
    index: number,
  ) => (
    <div
      className={styles.fileNameContainer}
      key={`${selectedFile.docType}-${selectedFile.file.name}-${index}`}
    >
      <IconFileWithItemsOutline
        width={20}
        height={20}
        className={styles.icon}
      />
      <p className={styles.name}>{selectedFile.file.name}</p>
      <Button
        data-testid="remove-button"
        className={styles.removeButton}
        size="small"
        variant="tertiary"
        disabled={disabled}
        onClick={() => handleRemove(index)}
      >
        <IconPlus height={20} width={20} className={styles.removeIcon} />
      </Button>
    </div>
  );

  const uploadedStatus = (
    <div className={styles.uploadedText}>
      <IconUpload width={16} height={16} className={styles.icon} />
      <span>{uploadedLabel}</span>
    </div>
  );

  return (
    <div className={styles.fileDropContainer}>
      <Label className={clsx({ [styles.labelNoInstruction]: !instruction })}>
        {label}
      </Label>
      {instruction && <div className={styles.instruction}>{instruction}</div>}

      {showFileDrop && (
        <>
          <FileDrop
            multiple={multiple}
            className={clsx(styles.fileDrop, fileDropClassName)}
            accept=".jpg, .jpeg, .png, .pdf"
            data-testid={`${docType}-file-upload`}
            onChange={handleOnChange}
            stacked={false}
            disabled={disabled}
          />
          {errorMessage && (
            <ErrorMessage className={styles.errorMessage}>
              {errorMessage}
            </ErrorMessage>
          )}
        </>
      )}

      {multiple
        ? selectedFiles.length > 0 && (
            <Card
              className={clsx(styles.card, styles.multiCard, cardClassName)}
            >
              <div className={styles.fileList}>
                {selectedFiles.map(renderFileRow)}
              </div>
              {uploadedStatus}
            </Card>
          )
        : selectedFiles.map((selectedFile, index) => (
            <Card
              key={`${selectedFile.docType}-${selectedFile.file.name}-${index}`}
              className={clsx(styles.card, cardClassName)}
            >
              {renderFileRow(selectedFile, index)}
              {uploadedStatus}
            </Card>
          ))}
    </div>
  );
};
